
import { defineComponent } from 'vue'

interface ZOptionPropsT extends Vue {
  value: string | number | null
  label: string
}

export default defineComponent({
  name: 'ZSelect',
  props: {
    selected: {
      type: [String, Number],
      default: null
    },
    tabIndex: {
      type: Number,
      required: false,
      default: 0
    },
    placeholder: {
      type: String,
      default: 'Select an Option'
    },
    clearable: {
      type: Boolean,
      default: false
    },
    spacing: {
      type: String,
      default: 'px-2 py-3'
    },
    borderClass: {
      type: String,
      default: 'border-ink-200'
    },
    borderRadius: {
      type: String,
      default: 'rounded-md'
    },
    backgroundClass: {
      type: String,
      default: 'bg-transparent'
    },
    textSize: {
      type: String,
      default: 'text-xs'
    },
    disabled: {
      default: false,
      type: Boolean
    },
    readOnly: {
      default: false,
      type: Boolean
    },
    truncate: {
      default: false,
      type: Boolean
    },
    maxHeight: {
      type: String,
      default: 'max-h-64',
      validator: (value: string) => value.startsWith('max-h')
    }
  },

  model: {
    prop: 'selected',
    event: 'change'
  },

  data() {
    return {
      open: false,
      options: [] as Array<Vue>
    }
  },

  computed: {
    canClick(): string {
      if (!this.disabled && !this.readOnly) return 'click'
      return ''
    },
    getTextSize(): string {
      const validTextSizes = ['text-xs', 'text-sm', 'text-base', 'text-lg', 'text-xl']
      if (validTextSizes.includes(this.textSize)) {
        return this.textSize
      }
      return 'text-xs'
    },
    getTextColor(): string {
      if (this.disabled) return 'text-slate'
      if (this.readOnly) return 'text-vanilla-400'
      return 'text-vanilla-300'
    },
    getCursorType(): string {
      if (this.disabled || this.readOnly) return 'cursor-not-allowed'
      return 'cursor-pointer'
    },
    getIconColor(): string {
      if (this.disabled) return 'slate'
      return 'vanilla-400'
    },
    selectedLabel(): string | null {
      return this.selectedOption?.label ?? null
    },
    selectedOption(): ZOptionPropsT | null {
      if (this.selected) {
        const selectedOption = this.options
          .map((child) => {
            return child.$options.propsData as ZOptionPropsT
          })
          .find((childProp) => {
            return childProp.value === this.selected
          })

        if (selectedOption) {
          return selectedOption
        }
      }

      return null
    },
    selectedValue(): string | number | null {
      return this.selectedOption?.value ?? null
    }
  },

  mounted(): void {
    // @ts-expect-error - investigate the infinitely typed issue
    this.options = this.$children.filter((child) => child.$options.name === 'ZOption')
  },

  methods: {
    blurEvent(): void {
      this.open = false
    },
    clearSelected(): void {
      this.$emit('change', null)
    },
    onChange(val: string): void {
      this.$emit('change', val)
    }
  }
})
